import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import { ActionMeta, components, GroupBase, MultiValue, MultiValueGenericProps, OptionProps, OptionsOrGroups, SingleValue } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { FormFeedback } from 'reactstrap';
import { shallowEqual } from 'shallow-equal-object';

import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { KeyboardKeys } from '@app/AppConstants/KeyboardKeys';
import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';
import { PersonFilterModel } from '@app/Models';
import { PersonLookupModel, PersonModel, PersonSortOrder } from '@app/Models/WebApiModels';

import Extensions from '@app/Services/Extensions';
import ApiService from '@app/Services/ApiService';
import { MatchComparer, stringSearchService } from '@app/Services/StringSearchService';
import { IPersonStore } from './VirtualizedRoster';
import { applyDecorators } from "@app/Helpers/Decorator";

type PersonFilterProps = {
    className?: string;
    title?: string;
    placeholder?: string;
    value: string;
    size?: string;
    validationErrors?: string[];
    isMulti?: boolean;
    filters?: PersonFilterModel;
    canClear?: boolean;
    includeInactive?: boolean;
    includeWithoutEmail?: boolean;
    personStore?: IPersonStore;
    personSortOrder?: PersonSortOrder;
    isPersonDisabled?: (person: PersonLookupModel | PersonModel) => boolean;
    onChangePersons?: (persons: PersonSelectDTO | PersonSelectDTO[]) => void;
    onSelect?: (personIds: string, persons: PersonSelectDTO[]) => void;
    renderCustomOption?: (props: OptionProps<PersonSelectDTO>, highlightedSearchText: JSX.Element) => JSX.Element;
};

@observer
export default class PersonFilter extends React.Component<PersonFilterProps> {
    @observable private _needToReloadOptions: boolean = false;
    private _store: PersonFilterStore;

    constructor(props: PersonFilterProps) {
        super(props);
        applyDecorators(this);
        this._store = new PersonFilterStore(props);
    }

    componentDidMount() {
        void this._store.loadPersonById();
    }

    componentDidUpdate(prevProps: PersonFilterProps) {
        this._store.updateProps(this.props);
        this._needToReloadOptions = false;

        if (!shallowEqual(prevProps.filters, this.props.filters) || prevProps.value !== this.props.value) {
            this._needToReloadOptions = true;
            if (this.props.value) {
                Extensions.executeTimeout(this._store.loadPersonById, 50);
            } else {
                this._store.selectedPersons = [];
            }
        }
    }

    render() {
        const { title, className, placeholder, canClear, isMulti, validationErrors, size } = this.props;
        const itemClass = ['filters-list-item'];
        if (size === 'sm') itemClass.push('size-sm');
        if (validationErrors?.length) itemClass.push('is-invalid');
        if (className) itemClass.push(className);

        return (
            <div className={itemClass.join(' ')}>
                {title && <div className="filters-list-item-title">{title}</div>}
                <div className="filters-list-item-field">
                    <AsyncPaginate
                        key={JSON.stringify(this._needToReloadOptions)}
                        placeholder={placeholder ?? 'None'}
                        backspaceRemovesValue={true}
                        closeMenuOnSelect={!isMulti}
                        isMulti={isMulti}
                        hideSelectedOptions={false}
                        debounceTimeout={500}
                        loadOptions={this._onLoadOptions}
                        onChange={this._onChangeHandler}
                        onKeyDown={this._onKeyDownHandler}
                        onInputChange={this._onInputChangeHandler}
                        onMenuClose={this._onMenuClose}
                        getOptionLabel={(option) => `(${option.code}) ${option.name}`}
                        getOptionValue={(option) => option.code}
                        tabSelectsValue={true}
                        escapeClearsValue={true}
                        className="select-filter-container select-filter-single"
                        classNamePrefix="person select-filter"
                        isClearable={canClear ?? true}
                        value={this._store.selectedPersons}
                        inputValue={this._store.search}
                        isOptionDisabled={(option) => option.disabled}
                        components={{ Option: this.renderOption, MultiValueLabel: this.renderMultiValueLabel }}
                    />
                    {validationErrors && !!validationErrors.length && (
                        <FormFeedback>
                            {validationErrors && validationErrors.map((error: string) => (
                                <div key={error}>{error}</div>
                            ))}
                        </FormFeedback>
                    )}
                </div>
            </div>
        );
    }

    renderOption = (props: OptionProps<PersonSelectDTO>) => {
        const highlightedSearchText = this._renderText(props.label, props.data.isInactive && !this.props.renderCustomOption);

        if (this.props.renderCustomOption) {
            return this.props.renderCustomOption(props, highlightedSearchText);
        }

        return (
            <components.Option {...props} >
                {props.isSelected ? props.label : highlightedSearchText}
            </components.Option>
        );
    };

    renderMultiValueLabel = (props: MultiValueGenericProps<PersonSelectDTO>) => {
        return <components.MultiValueLabel {...props}>{props.data.code}</components.MultiValueLabel>;
    };

    @action.bound
    private _onLoadOptions(search: string, options: OptionsOrGroups<PersonSelectDTOValue, GroupBase<PersonSelectDTOValue>>) {
        return this._store.loadOptions(search, options, this.props);
    }

    @action.bound
    private _onMenuClose() {
        this._store.search = '';

        if (this.props.isMulti) {
            this._onSelectPersons();
        }
    }

    @action.bound
    private _onChangeHandler(selectedPersons: PersonSelectDTOValue, actionMeta?: ActionMeta<PersonSelectDTO>) {
        const persons: PersonSelectDTO[] = Array.isArray(selectedPersons)
            ? selectedPersons
            : selectedPersons ? [selectedPersons] : [];

        this._store.selectedPersons = persons;
        this.props.onChangePersons?.(persons);

        if (!this.props.isMulti || actionMeta?.action === 'clear') {
            this._onSelectPersons();
        }
    }

    @action
    private _onSelectPersons() {
        const personIds = this._store.selectedPersons.map((p) => p.id).join(',');
        this.props.onSelect?.(personIds, this._store.selectedPersons);
    }

    @action.bound
    private _onKeyDownHandler(event: React.KeyboardEvent<HTMLElement>) {
        const { isMulti } = this.props;

        if (event.keyCode === KeyboardKeys.COMMA) {
            const searchValue = (event.target as HTMLInputElement).value;
            const result = this._store.saveFromSearch(searchValue, isMulti || false);
            this._store.search = '';
            this._onChangeHandler(result);
        }
    }

    @action.bound
    private _onInputChangeHandler(search: string) {
        const modifiedInput = search.charAt(search.length - 1) === ',' ? '' : search;
        this._store.search = modifiedInput;
    }

    @computed
    private get _searchTerms() {
        const searchStr = this._store.search;
        return searchStr.split(/[ ,]+/).filter((x) => !!x);
    }

    @action
    private _renderText(text: string | undefined, decorate?: boolean) {
        const comparator: MatchComparer = (v, t, s) => {
            return stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
        };
        const parts = stringSearchService.markMatches(text, this._searchTerms, comparator);
        return (
            <span style={{textDecoration: decorate ? 'line-through' : undefined }}>
                {parts.map((p, index) =>
                    p.isMatch ? (
                        <span className='text-match' key={'t' + index}>
                            {`${p.text}`}
                        </span>
                    ) : (
                        p.text
                    )
                )}
            </span>
        );
    }
}

class PersonFilterStore {
    @observable.ref private _options: PersonSelectDTO[] = [];
    @observable.ref public selectedPersons: PersonSelectDTO[] = [];
    @observable public search: string = '';
    private _props: PersonFilterProps;

    constructor(props: PersonFilterProps) {
        applyDecorators(this);
        this.updateProps(props);
    }

    public updateProps(props: PersonFilterProps) {
        this._props = props;
    }

    @action
    public async loadOptions(search: string, prevOptions: OptionsOrGroups<PersonSelectDTOValue, GroupBase<PersonSelectDTOValue>>, props: PersonFilterProps) {
        const pageSize = 30;
        const page = prevOptions.length % pageSize === 0 ? (prevOptions.length + pageSize) / pageSize : 1;

        const sendData: PersonFilterModel = props.filters ? Object.assign({}, props.filters) : ({} as PersonFilterModel);
        if (search) {
            sendData.search = search;
            sendData.searchByFullName = true;
        }
        if (sendData.functionId === FunctionGroupIds.Fcm) {
            sendData.functionId = null;
            sendData.functionIds = [FunctionGroupIds.Cabin, FunctionGroupIds.Cockpit];
        }

        const params = {
            ids: this._props.personStore ? this._props.personStore.filteredPersons.map(p => p.id) : sendData.ids?.trim() ? sendData.ids.split(',') : [],
            functionId: sendData.functionId,
            functionIds: sendData.functionIds,
            referenceDate: sendData.referenceDate,
            qualificationIds: sendData.qualificationIds?.trim() ? sendData.qualificationIds.split(',') : [],
            contractTypes: sendData.contractTypes?.trim() ? sendData.contractTypes.split(',').map(x => +x) : [],
            partialLoadType: sendData.partialLoadType,
            dutyFilter: sendData.dutyFilter,
            monthlyTypes: sendData.monthlyTypes?.trim() ? sendData.monthlyTypes.split(',').map(x => +x) : [],
            releaseFilterTypes: sendData.releaseFilterTypes?.trim() ? sendData.releaseFilterTypes.split(',').map(x => +x) : [],
            functionFromDate: sendData.functionFromDate,
            search: sendData.search,
            searchByFullName: sendData.searchByFullName,
            pageSize: pageSize,
            page: page,
            includeInactive: props.includeInactive ?? sendData.includeInactive,
            includeWithoutEmail: props.includeWithoutEmail ?? sendData.includeWithoutEmail,
            sort: props.personSortOrder ?? sendData.sort ?? undefined,
            order: sendData.order ?? undefined
        };

        
        const { data } = await ApiService.postTypedData<PersonLookupModel[]>(ApiUrls.PersonLookupUrl, params);
        const persons = data.map((item) => {
            const isDisabled = props.isPersonDisabled ? props.isPersonDisabled(item) : false;
            return new PersonSelectDTO(item, isDisabled);
        });

        this._options = persons;

        return {
            options: persons,
            hasMore: persons.length === pageSize
        };
    }

    @action.bound
    public async loadPersonById() {
        const { value, includeInactive, includeWithoutEmail, isPersonDisabled, filters } = this._props;

        if (!value) return;

        const params = {
            ids: value,
            includeInactive: includeInactive,
            includeWithoutEmail: includeWithoutEmail,
            functionFromDate: filters?.functionFromDate,
            referenceDate: filters?.referenceDate
        };
        const { data } = await ApiService.getTypedData<PersonModel[]>(ApiUrls.PersonUrl, params);
        this.selectedPersons = data.map(item => {
            const isDisabled = isPersonDisabled ? isPersonDisabled(item) : false;
            return new PersonSelectDTO(item, isDisabled);
        });

        this._props.onChangePersons?.(this.selectedPersons);
    }

    @action
    saveFromSearch(searchValue: string, isMulti: boolean) {
        let selectedPersons = this.selectedPersons;
        if (searchValue.replace(',', '').trim().length === 0) return selectedPersons;

        const persons = this._options;
        const codes = searchValue.split(',').map((code: string) => code.trim().toUpperCase());
        const matchedList = persons.filter((item: PersonSelectDTO) => codes.indexOf(item.code.toUpperCase()) !== -1);

        if (!matchedList || matchedList.length === 0) return selectedPersons;

        if (isMulti) {
            matchedList.forEach((person) => {
                if (selectedPersons.findIndex(p => person.id === p.id) < 0) {
                    selectedPersons.push(person);
                }
            });
        } else {
            selectedPersons = matchedList;
        }

        return selectedPersons;
    }
}

type PersonSelectDTOValue = SingleValue<PersonSelectDTO> | MultiValue<PersonSelectDTO>;
export class PersonSelectDTO {
    name: string;
    id: string;
    disabled: boolean;
    code: string;
    email: string;
    isInactive: boolean;

    constructor(item: Partial<PersonModel>, isDisabled?: boolean) {
        this.name = `${item.firstName} ${item.lastName}`;
        this.id = item.id || '';
        this.disabled = !!isDisabled;
        this.code = item.code || '';
        this.email = item.email || '';
        this.isInactive = !(item.isActive ?? true);
    }
}
